import { noop } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { useUuid } from '~/eds/src/hooks/useUuid';
import { TextEditor } from '~/eds/src/modules/TextEditor/TextEditor';
import { ContentAst } from '~/eds/src/types/ContentAst';
import {
  parseContentAst,
  serializeContentAst,
  testIsEmptyAst,
} from '~/eds/src/utils/contentAst';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useErrors } from '~/hooks/useErrors';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

import { PROMPT_WORD_LIMIT } from '../../constants';
import { testIsPromptOverLimit } from '../../utils/ui';

interface Props {
  value: Nullable<string>;
  onChange: (updatedContent: string) => void;
  placeholder?: string;
  readOnly?: boolean;
}

export const InstructionsEditor = ({
  onChange,
  placeholder,
  value,
  readOnly = false,
}: Props) => {
  const intl = useIntl();
  const { RETRY_ERROR } = useErrors();
  const { params } = useRouting();
  const { modelId = '' } = params;
  const { toast } = useToast();
  const enableOptimize = useFlag(FlagType.DocumentXrayOptimize);

  const config = useSelector(slices.xRay.selectors.selectConfiguration);
  const field = useSelector(slices.xRay.selectors.selectField);

  const [isEditing, setIsEditing] = useState(false);
  const [uuid, setUuid] = useUuid();
  const content = useMemo(() => parseContentAst(value ?? ''), [value]);
  const [draftContent, setDraftContent] = useState<ContentAst>(content);
  const [isAiSuggestion, setIsAiSuggestion] = useState(false);

  const [
    getSuggestion,
    {
      data: aiSuggestion = '',
      isLoading: isLoadingSuggestion,
      isFetching: isFetchingSuggestion,
    },
  ] = api.endpoints.suggestPromptModelInstructions.useLazyQuery();

  // sync when props.value (content) changes.  Slate does not support controlled usage, so we update with a new generated UUID key.
  useEffect(() => {
    setDraftContent(content);
    setUuid();
  }, [content]);

  const handleSave = () => {
    setIsEditing(false);
    const updatedValue = serializeContentAst(draftContent);
    onChange(updatedValue);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setUuid();
    setDraftContent(content);
  };

  const handleAiSuggest = () => {
    setIsAiSuggestion(true);
    getSuggestion({
      config,
      modelId,
    })
      .then(() => {
        setUuid();
      })
      .catch(() => {
        toast({
          status: 'danger',
          message: RETRY_ERROR,
        });
        setIsAiSuggestion(false);
      });
  };

  const handleAiAccept = () => {
    setIsAiSuggestion(false);
    setIsEditing(false);
    setDraftContent(parseContentAst(aiSuggestion));
    onChange(aiSuggestion);
  };

  const handleAiReject = () => {
    setIsAiSuggestion(false);
    setUuid();
  };

  const disableAiSuggest = modelId !== 'new' && enableOptimize;

  const isEmptyContent = testIsEmptyAst(content);
  const isEmptyDraftContent = testIsEmptyAst(draftContent);
  const enableSaveCancelActions =
    !isAiSuggestion &&
    isEditing &&
    (!isEmptyDraftContent || (isEmptyDraftContent && !isEmptyContent));

  const status = isEditing ? (isAiSuggestion ? 'ai' : undefined) : 'inactive';
  const suggestionIsLoading = isLoadingSuggestion || isFetchingSuggestion;
  const displayContent =
    isAiSuggestion && !suggestionIsLoading
      ? parseContentAst(aiSuggestion)
      : draftContent;

  const overlimitTooltip = testIsPromptOverLimit(
    serializeContentAst(displayContent),
  )
    ? intl.formatMessage(
        {
          id: 'InstructionsEditor.instructionsOverLimit',
          defaultMessage: 'Instructions must be {limit} words or fewer.',
          description:
            'Tooltip warning when instructions exceed word limit. Example: limit = "100"',
        },
        { limit: PROMPT_WORD_LIMIT },
      )
    : undefined;

  const actions = [
    ...(!isEditing
      ? [
          {
            disabled: readOnly,
            tooltip: readOnly
              ? intl.formatMessage({
                  id: 'InstructionsEditor.readOnlyMode',
                  defaultMessage: 'You are in read-only mode.',
                  description: 'Tooltip when editor is in read-only mode',
                })
              : undefined,
            icon: 'edit' as const,
            iconPosition: 'left' as const,
            text: intl.formatMessage({
              id: 'InstructionsEditor.edit',
              defaultMessage: 'Edit',
              description: 'Button text for edit action',
            }),
            onClick: () => setIsEditing(true),
          },
        ]
      : []),
    ...(field && isEditing
      ? [
          {
            disabled: disableAiSuggest,
            icon: isAiSuggestion ? ('rerun' as const) : ('ai' as const),
            iconPosition: 'left' as const,
            status: 'ai' as const,
            text: intl.formatMessage({
              id: 'InstructionsEditor.suggestInstructions',
              defaultMessage: 'Suggest Instructions',
              description: 'Button text for AI instruction suggestion',
            }),
            tooltip: disableAiSuggest
              ? intl.formatMessage({
                  id: 'InstructionsEditor.suggestInstructionsDisabled',
                  defaultMessage:
                    'Evaluate documents and use AI Boost to suggest better instructions.',
                  description: 'Tooltip when AI suggest is disabled',
                })
              : undefined,
            onClick: handleAiSuggest,
          },
        ]
      : []),
    ...(isAiSuggestion
      ? [
          {
            disabled: Boolean(overlimitTooltip),
            icon: 'check' as const,
            iconPosition: 'left' as const,
            text: intl.formatMessage({
              id: 'InstructionsEditor.accept',
              defaultMessage: 'Accept',
              description: 'Button text for accepting AI suggestion',
            }),
            tooltip: overlimitTooltip,
            onClick: handleAiAccept,
          },
          {
            icon: 'x' as const,
            iconPosition: 'left' as const,
            text: intl.formatMessage({
              id: 'InstructionsEditor.reject',
              defaultMessage: 'Reject',
              description: 'Button text for rejecting AI suggestion',
            }),
            onClick: handleAiReject,
          },
        ]
      : []),
    ...(enableSaveCancelActions
      ? [
          {
            disabled: Boolean(overlimitTooltip),
            icon: 'check' as const,
            iconPosition: 'left' as const,
            text: intl.formatMessage({
              id: 'InstructionsEditor.save',
              defaultMessage: 'Save',
              description: 'Button text for save action',
            }),
            tooltip: overlimitTooltip,
            onClick: handleSave,
          },
          {
            icon: 'x' as const,
            iconPosition: 'left' as const,
            text: intl.formatMessage({
              id: 'InstructionsEditor.cancel',
              defaultMessage: 'Cancel',
              description: 'Button text for cancel action',
            }),
            onClick: handleCancel,
          },
        ]
      : []),
  ];

  return (
    <TextEditor
      key={uuid}
      disablePostButton
      actions={suggestionIsLoading ? [] : actions}
      content={displayContent}
      enableMention={false}
      enableResize={true}
      limit={PROMPT_WORD_LIMIT}
      limitMode="words"
      loadingContent={{
        isLoading: suggestionIsLoading,
        message: intl.formatMessage({
          id: 'InstructionsEditor.loadingMessage',
          defaultMessage: 'Loading… This may take a minute or more.',
          description: 'Loading message for AI suggestion generation',
        }),
      }}
      placeholder={placeholder}
      readOnly={!isEditing || isAiSuggestion}
      status={status}
      onChange={setDraftContent}
      onPost={noop}
    />
  );
};
